// Mount harness for input-island React components.
//
// The Shiny side renders one DOM slot per group whose `kind` requires
// React. Each slot carries:
//   - `data-react-input`        — the kind name (e.g. "curve-editor")
//   - kind-specific `data-*`    — props the React component needs
//
// This harness scans for those slots on shiny:connected (after Shiny
// has rendered the modules client-side) and mounts the right component
// into each. The `data-mounted` flag prevents double-mounts when the
// event fires more than once.
//
// Adding a new input-island kind = add a key + factory to KNOWN_EDITORS.
// Everything else (slot discovery, mount lifecycle) is shared.

import { StrictMode } from "react";
import type { ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { CurveEditor } from "./curve_editor";
import { BracketEditor } from "./bracket_editor";
import { DeltaSlider } from "./delta_slider";
import { AbsoluteSlider } from "./absolute_slider";
import { CategoricalAssignment } from "./categorical_assignment";
import type {
  CurveEditorProps,
  BracketEditorProps,
  DeltaSliderProps,
  AbsoluteSliderProps,
  AbsoluteFormat,
  CategoricalAssignmentProps,
  CategoricalBand,
  CategoricalCategory,
} from "./types";

type Factory = (slot: HTMLElement) => ReactNode;

const KNOWN_EDITORS: Record<string, Factory> = {
  "curve-editor": (slot) => <CurveEditor {...readCurveEditorProps(slot)} />,
  "bracket-editor": (slot) => (
    <BracketEditor {...readBracketEditorProps(slot)} />
  ),
  "delta-slider": (slot) => <DeltaSlider {...readDeltaSliderProps(slot)} />,
  "absolute-slider": (slot) => (
    <AbsoluteSlider {...readAbsoluteSliderProps(slot)} />
  ),
  "categorical-assignment": (slot) => (
    <CategoricalAssignment {...readCategoricalAssignmentProps(slot)} />
  ),
};

export function mountInputIslands(): void {
  const slots = document.querySelectorAll<HTMLElement>("[data-react-input]");
  slots.forEach((slot) => {
    if (slot.dataset.mounted === "true") return;
    const kind = slot.dataset.reactInput ?? "";
    const factory = KNOWN_EDITORS[kind];
    if (!factory) {
      // Loud at the console so a typo in the YAML spec is visible in
      // dev tools rather than silently rendering nothing.
      // eslint-disable-next-line no-console
      console.warn(`Unknown input-island kind: ${kind}`);
      return;
    }
    slot.dataset.mounted = "true";
    createRoot(slot).render(<StrictMode>{factory(slot)}</StrictMode>);
  });
}

function readDeltaSliderProps(slot: HTMLElement): DeltaSliderProps {
  const d = slot.dataset;
  return {
    inputId: required(d.inputId, "input-id"),
    label: required(d.label, "label"),
    instrument: d.instrument || "",
    description: d.description || undefined,
    defaultValue: parseNumber(d.default, "default"),
    min: parseNumber(d.min, "min"),
    max: parseNumber(d.max, "max"),
    step: parseNumber(d.step, "step"),
    postfix: d.postfix || undefined,
  };
}

function readAbsoluteSliderProps(slot: HTMLElement): AbsoluteSliderProps {
  const d = slot.dataset;
  return {
    inputId: required(d.inputId, "input-id"),
    label: required(d.label, "label"),
    instrument: d.instrument || "",
    description: d.description || undefined,
    defaultValue: parseNumber(d.default, "default"),
    min: parseNumber(d.min, "min"),
    max: parseNumber(d.max, "max"),
    step: parseNumber(d.step, "step"),
    format: parseFormat(d.format),
  };
}

function parseFormat(value: string | undefined): AbsoluteFormat {
  if (value === "euro" || value === "percent" || value === "number") {
    return value;
  }
  throw new Error(
    `Input island mount slot data-format must be euro|percent|number, got: ${value}`,
  );
}

function readCurveEditorProps(slot: HTMLElement): CurveEditorProps {
  const d = slot.dataset;
  return {
    inputId: required(d.inputId, "inputId"),
    label: required(d.label, "label"),
    instrument: d.instrument || "",
    description: d.description || undefined,
    defaultValue: parseNumber(d.default, "default"),
    min: parseNumber(d.min, "min"),
    max: parseNumber(d.max, "max"),
    step: parseNumber(d.step, "step"),
    targets: parseStringArray(d.targets, "targets"),
    xAxisData: parseNumberArray(d.xAxisData, "x-axis-data"),
    yAxisData: parseNumberArray(d.yAxisData, "y-axis-data"),
    xAxisLabel: required(d.xAxisLabel, "x-axis-label"),
    yAxisLabel: required(d.yAxisLabel, "y-axis-label"),
  };
}

function readBracketEditorProps(slot: HTMLElement): BracketEditorProps {
  const d = slot.dataset;
  return {
    inputId: required(d.inputId, "input-id"),
    label: required(d.label, "label"),
    instrument: d.instrument || "",
    description: d.description || undefined,
    boundaryTargets: parseStringArray(d.boundaryTargets, "boundary-targets"),
    rateTargets: parseStringArray(d.rateTargets, "rate-targets"),
    boundaryDefaults: parseNumberArray(d.boundaryDefaults, "boundary-defaults"),
    rateDefaults: parseNumberArray(d.rateDefaults, "rate-defaults"),
    boundaryMin: parseNumber(d.boundaryMin, "boundary-min"),
    boundaryMax: parseNumber(d.boundaryMax, "boundary-max"),
    boundaryStep: parseNumber(d.boundaryStep, "boundary-step"),
    rateMin: parseNumber(d.rateMin, "rate-min"),
    rateMax: parseNumber(d.rateMax, "rate-max"),
    rateStep: parseNumber(d.rateStep, "rate-step"),
    xAxisLabel: required(d.xAxisLabel, "x-axis-label"),
    yAxisLabel: required(d.yAxisLabel, "y-axis-label"),
  };
}

function readCategoricalAssignmentProps(
  slot: HTMLElement,
): CategoricalAssignmentProps {
  const d = slot.dataset;
  return {
    inputId: required(d.inputId, "input-id"),
    label: required(d.label, "label"),
    instrument: d.instrument || "",
    description: d.description || undefined,
    bands: parseBands(d.bands, "bands"),
    categories: parseCategories(d.categories, "categories"),
  };
}

function parseBands(value: string | undefined, name: string): CategoricalBand[] {
  const arr = JSON.parse(required(value, name)) as unknown;
  if (
    !Array.isArray(arr) ||
    !arr.every(
      (b) =>
        typeof b === "object" &&
        b !== null &&
        typeof (b as { id?: unknown }).id === "string" &&
        typeof (b as { rate?: unknown }).rate === "number",
    )
  ) {
    throw new Error(
      `Input island mount slot data-${name} must be a JSON array of { id, rate }`,
    );
  }
  return (arr as Array<{ id: string; rate: number }>).map((b) => ({
    id: b.id,
    rate: b.rate,
  }));
}

function parseCategories(
  value: string | undefined,
  name: string,
): CategoricalCategory[] {
  const arr = JSON.parse(required(value, name)) as unknown;
  const ok = (c: unknown): c is { id: string; label: string; default_band: string; section?: unknown } =>
    typeof c === "object" &&
    c !== null &&
    typeof (c as { id?: unknown }).id === "string" &&
    typeof (c as { label?: unknown }).label === "string" &&
    typeof (c as { default_band?: unknown }).default_band === "string";
  if (!Array.isArray(arr) || !arr.every(ok)) {
    throw new Error(
      `Input island mount slot data-${name} must be a JSON array of { id, label, default_band, section }`,
    );
  }
  return arr.map((c) => ({
    id: c.id,
    label: c.label,
    section: typeof c.section === "string" ? c.section : "",
    defaultBand: c.default_band,
  }));
}

function required(value: string | undefined, name: string): string {
  if (value === undefined || value === "") {
    throw new Error(`Input island mount slot is missing data-${name}`);
  }
  return value;
}

function parseNumber(value: string | undefined, name: string): number {
  const n = Number(required(value, name));
  if (Number.isNaN(n)) {
    throw new Error(`Input island mount slot data-${name} is not a number: ${value}`);
  }
  return n;
}

function parseNumberArray(value: string | undefined, name: string): number[] {
  const arr = JSON.parse(required(value, name)) as unknown;
  if (!Array.isArray(arr) || !arr.every((v) => typeof v === "number")) {
    throw new Error(`Input island mount slot data-${name} must be a JSON number array`);
  }
  return arr;
}

function parseStringArray(value: string | undefined, name: string): string[] {
  const arr = JSON.parse(required(value, name)) as unknown;
  if (!Array.isArray(arr) || !arr.every((v) => typeof v === "string")) {
    throw new Error(`Input island mount slot data-${name} must be a JSON string array`);
  }
  return arr;
}
